import { InteractionMap } from "../view/interactions";
import { ModuleID } from "../types/ids";
import {
  ErrorModuleRef,
  ModuleRef,
  ModulesConfig,
  ViewModuleRef,
} from "../view/types";
import { VersionString } from "../manifest/types";
import {
  ErrorModuleIdArg,
  ErrorModuleVersionArg,
  ModuleIdArg,
  ModuleVersionArg,
  RegisteredModuleProps,
} from "./registry";

// ============================================================================
// module() helper
// ============================================================================

/**
 * Type-safe module reference helper, bound to a view's data/state/interaction
 * types and generic per call over the referenced module's id and pinned
 * version. Props are checked against the {@link ModuleRegistry} contract for
 * exactly that id + version.
 *
 * This is the type of the `mod` argument passed to a view's `modules` and
 * `layoutOverride` callbacks. The standalone {@link moduleRef} export is an
 * instance with the view-context types left at their `unknown` defaults.
 * For flow-level `layout`, use {@link flowModuleRef}; for `errorView`, use
 * {@link errorModuleRef}.
 *
 * Until a module registers, `ModuleRegistry` is empty and `keyof ModuleRegistry`
 * is `never`, so `module()` rejects every id — there is no valid module id until
 * one exists. Views load the populated registry via tsconfig `types` and see the
 * real id / version / prop contracts.
 *
 * @example
 * ```ts
 * // Static props — plain object, no view context needed:
 * modules: mod => [
 *   mod("sample/sample-form", "1.1.0", { title: "My Form" }),
 * ],
 *
 * // Callback props — receive a fully-typed {@link ModulesConfig}: `interactions`
 * // are wired handlers, plus server `data`, `viewState`, and `setViewState`:
 * modules: mod => [
 *   mod("sample/sample-form", "1.1.0", ({ interactions, viewState, data }) => ({
 *     title: viewState.formTitle,
 *     onSubmit: interactions.submitForm,
 *     defaultEmail: data.user.email,
 *   })),
 * ],
 *
 * // layoutOverride returns a single ModuleRef:
 * layoutOverride: mod =>
 *   mod("shared/main-layout", "1.0.0", ({ data }) => ({ showHeader: data.isLoggedIn })),
 * ```
 */
export type ModuleHelper<
  TServerData = unknown,
  TViewState = unknown,
  TInteractions extends InteractionMap = InteractionMap,
> = <TId extends ModuleIdArg, TVersion extends ModuleVersionArg<TId>>(
  moduleId: TId,
  minVersion: TVersion,
  // Props may be a plain record or a callback receiving a fully-typed
  // ModulesConfig; either way it must satisfy the registry contract for the
  // referenced id + pinned version.
  props:
    | RegisteredModuleProps<TId, TVersion>
    | ((
        config: ModulesConfig<TServerData, TViewState, TInteractions>,
      ) => RegisteredModuleProps<TId, TVersion>),
  fetcherConfig?: unknown,
) => ViewModuleRef<TServerData, TViewState, TInteractions>;

/**
 * Create a `ModuleRef` — a reference to a module with props and an optional
 * fetcher config. Typed by {@link ModuleHelper}; the runtime just packages the
 * arguments into a `ModuleRef`.
 *
 * Use inside `defineView` via the pre-bound `mod` argument so the view's context
 * types flow into prop callbacks. For flow-level refs use {@link flowModuleRef}
 * or {@link errorModuleRef}.
 *
 * Typed `ModuleHelper<any, any, any>` so a view of *any* context type can accept
 * it as its `modules` / `layoutOverride` argument — the view binds its own
 * context through the parameter's `ModuleHelper<TServerData, …>`, so the `any`
 * here never weakens in-view prop typing.
 */
function buildModuleRef(
  moduleId: ModuleID,
  minVersion: VersionString,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- runtime body only packages already-validated args; strict contracts are enforced by callers
  props: any,
  fetcherConfig?: unknown,
) {
  return {
    _tag: "ModuleRef" as const,
    moduleId,
    minVersion,
    props,
    fetcherConfig,
  };
}

function createModuleRef(
  moduleId: ModuleID,
  minVersion: VersionString,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- runtime body only packages already-validated args; the strict contract (id → version → props) is enforced by the ModuleHelper type below
  props: any,
  fetcherConfig?: unknown,
): ViewModuleRef<unknown, unknown, InteractionMap> {
  return buildModuleRef(moduleId, minVersion, props, fetcherConfig);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- context generics are `any` so `moduleRef` is assignable to any view's ModuleHelper<S, V, I>; per-call id/version/props checking is unaffected
export const moduleRef: ModuleHelper<any, any, any> = createModuleRef;

export type ErrorModuleHelper = <
  TId extends ErrorModuleIdArg,
  TVersion extends ErrorModuleVersionArg<TId>,
>(
  moduleId: TId,
  minVersion: TVersion,
  props: RegisteredModuleProps<TId, TVersion>,
  fetcherConfig?: unknown,
) => ErrorModuleRef;

function createErrorModuleRef(
  moduleId: ModuleID,
  minVersion: VersionString,
  // `unknown` — implementation must accept every `RegisteredModuleProps` variant
  // (including inferred `P` shapes that are not assignable to Record<string, unknown>
  // under strictFunctionTypes). Call-site checking stays on ErrorModuleHelper.
  props: unknown,
  fetcherConfig?: unknown,
): ErrorModuleRef {
  return buildModuleRef(
    moduleId,
    minVersion,
    props,
    fetcherConfig,
  ) as ErrorModuleRef;
}

export const errorModuleRef: ErrorModuleHelper = createErrorModuleRef;

// ============================================================================
// flowModuleRef() helper
// ============================================================================

/**
 * Type-safe module reference helper for flow-level `layout`.
 *
 * Like {@link moduleRef}, props are checked against the {@link ModuleRegistry}
 * contract for the referenced id + version. Unlike `moduleRef`:
 *
 * - Only **static** props are accepted (no view-context callbacks).
 * - Returns a plain {@link ModuleRef} (not {@link ViewModuleRef}), matching
 *   `Flow.layout`.
 *
 * For `errorView`, use {@link errorModuleRef} instead.
 */
export type FlowModuleHelper = <
  TId extends ModuleIdArg,
  TVersion extends ModuleVersionArg<TId>,
>(
  moduleId: TId,
  minVersion: TVersion,
  props: RegisteredModuleProps<TId, TVersion>,
  fetcherConfig?: unknown,
) => ModuleRef;

function createFlowModuleRef(
  moduleId: ModuleID,
  minVersion: VersionString,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- runtime body only packages already-validated args; the strict contract (id → version → props) is enforced by FlowModuleHelper
  props: any,
  fetcherConfig?: unknown,
): ModuleRef {
  return buildModuleRef(moduleId, minVersion, props, fetcherConfig);
}

export const flowModuleRef: FlowModuleHelper = createFlowModuleRef;
